import { PlantDto, ProductLine, UserDto, UserPlantDto } from '@trucktrax/trucktrax-ts-common';
import { Dispatch } from 'redux';

import { SET_PLANT_LIST, TOGGLE_MAP_FILTER } from '../constants/actionConstants';
import { GEOZONES_PATH, PLANTS_PATH } from '../constants/apiConstants';
import { TRUCKTRAX_GEO_FLAG, INVALID_STALE_TOKEN_LOGOUT } from '../constants/appConstants';
import { ERROR_TEXT_FETCH_PLANT, ERROR_TEXT_FETCH_PLANT_LIST } from '../constants/errorConstants';
import HTTP_CODES from '../constants/httpConstants';
import * as plantsActions from '../store/actions/plantsActions';
import { getGeotraxBaseUrl } from '../util/apiUtil';
import { getIdFromUrl } from '../util/appUtil';
import recordLog, { devErrorAndLog, LogLevel } from '../util/errorUtil';
import { isFeatureFlagEnabled } from '../util/featureFlagUtil';
import { getRequest } from './requestService';
import { getUserPreferences } from './userPreferencesService';
import { getUserByUrl } from './usersService';

// The plants GET. Propagates a failed request rather than reporting it as an empty list:
// nine of the eleven call sites already wrap this in try/catch, and until DEV-4427 those
// catches were unreachable. Omitting `region` returns every plant in the tenant.
//
// The failed request is already logged and classified before it reaches a caller:
// requestService.errorHandler runs recordLog(GENERAL_ERROR_HANDLER_TYPE, url, error) on an
// ordinary failure and turns a 401 into the INVALID_STALE_TOKEN_LOGOUT logout signal (a
// bare string). So this just lets the error through untouched — no extra recordLog here,
// which only duplicated errorHandler's and fired on every session expiry.
//
// History, because the shape here has flipped once already: this threw from creation
// (2021-06-22) until 44b85799d2 (2022-09-16) wrapped it in try/catch and returned [] on
// failure. That silently disabled the error paths in DriverDetailView, DriversAddModal and
// OrdersView, which were written against the throwing contract, and four callers added
// afterwards were written against it too and never worked. See DEV-4154 in particular.
//
// Callers that genuinely want best-effort should say so with an explicit
// `.catch(() => [])` at the call site, as userPlants does. Callers that surface the error
// must skip INVALID_STALE_TOKEN_LOGOUT so a routine session expiry doesn't read as a
// plants failure — see the thunks below and the admin screens.
export async function getPlantList(
  region?: string,
  productLine?: string,
  isDeleted?: boolean,
  isArchived?: boolean,
  inProductLines?: string,
) {
  const url = getGeotraxBaseUrl() + PLANTS_PATH;
  const params: {
    region?: string;
    productLine?: string;
    isDeleted?: boolean;
    isArchived?: boolean;
    inProductLines?: string;
  } = {};

  if (region) {
    params.region = region;
  }
  if (productLine) {
    params.productLine = productLine;
  }
  if (inProductLines) {
    params.inProductLines = inProductLines;
  }

  params.isDeleted = !!isDeleted;
  params.isArchived = !!isArchived;

  const response = await getRequest(url, params);
  return response && response.data ? response.data.items : [];
}

export function getClosestPlant(
  currentPlantId: number,
  latitude: number,
  longitude: number,
  region: string,
) {
  return async (dispatch: Dispatch) => {
    const url = getGeotraxBaseUrl() + PLANTS_PATH;
    const params = {
      latitude,
      longitude,
      region,
    };
    const fullUrl = `${url}/closest/${currentPlantId}`;
    try {
      const response = await getRequest(fullUrl, params);
      return response.data;
    } catch (e: any) {
      if (e !== INVALID_STALE_TOKEN_LOGOUT) {
        const consoleOnly = e.response?.status === HTTP_CODES.forbidden;
        dispatch(
          devErrorAndLog(
            e.toString(),
            ERROR_TEXT_FETCH_PLANT,
            e,
            `url: ${url} params: ${JSON.stringify(params)}`,
            consoleOnly,
          ) as any,
        );
      }
      return undefined;
    }
  };
}

export function getDefaultPlant(userUrl: string) {
  return async (dispatch: Dispatch) => {
    try {
      const user = (await getUserByUrl(userUrl)) as UserDto;
      // user assigned plants
      if (!user.plants) {
        return undefined;
      }
      // find the default assigned plant
      const defaultUserPlant = user.plants.find((p: UserPlantDto) => p.default);
      if (!defaultUserPlant) {
        return undefined;
      }
      // get the full DTO
      const url = `${getGeotraxBaseUrl() + PLANTS_PATH}/${getIdFromUrl(defaultUserPlant.url)}`;
      const params = {
        isArchived: false,
      };
      const response = await getRequest(url, params);
      return response.data;
    } catch (e: any) {
      if (e !== INVALID_STALE_TOKEN_LOGOUT) {
        const consoleOnly = e.response?.status === HTTP_CODES.forbidden;
        dispatch(
          devErrorAndLog(
            e.toString(),
            ERROR_TEXT_FETCH_PLANT,
            e,
            `userurl: ${userUrl}`,
            consoleOnly,
          ) as any,
        );
      }
      return undefined;
    }
  };
}

export function setCurrentPlant(plant?: PlantDto) {
  return (dispatch: Dispatch) => {
    dispatch(plantsActions.setCurrentPlant(plant));
  };
}

export function fetchPlantList(
  region?: string,
  selectedProductLine?: string,
  userUrl?: string,
  inProductLines?: string,
) {
  return async (dispatch: Dispatch) => {
    try {
      const response = await getPlantList(
        region,
        selectedProductLine,
        undefined,
        undefined,
        inProductLines,
      );
      let plants = [];
      // if a user is passed, filter the plants list by plants assigned to the user
      if (userUrl) {
        const user = (await getUserByUrl(userUrl)) as UserDto;
        plants =
          user.plants &&
          response.filter((el: { url: string | undefined }) =>
            user.plants!.some((f: UserPlantDto) => f.url === el.url),
          );
      } else {
        plants = response;
      }
      const plantFilter = plants
        ? plants.map((plant: any) => ({
            id: plant.id,
            name: plant.name,
            url: plant.url,
            isSelected: true,
          }))
        : [];
      dispatch({
        type: TOGGLE_MAP_FILTER,
        payload: { plantList: plantFilter },
      });
      dispatch({
        type: SET_PLANT_LIST,
        payload: plants,
      });
    } catch (e: any) {
      if (e === INVALID_STALE_TOKEN_LOGOUT) {
        return;
      }
      // Clear the slice on failure. Every consumer of this thunk — ScaleTraxTicketing,
      // ScalesAddModal/DetailView, RouteHistoryPanelSearchForm — reads state.plantList as
      // the option source for a record-creating or record-searching picker, so a failed
      // refetch after a region change must not leave the previous region's plants
      // selectable. (The map bootstrap uses fetchPlantListAndMapFilters, not this.) This is
      // the behaviour getPlantList's old swallow gave for free by resolving to [].
      dispatch({ type: TOGGLE_MAP_FILTER, payload: { plantList: [] } });
      dispatch({ type: SET_PLANT_LIST, payload: [] });
      const consoleOnly = e.response?.status === HTTP_CODES.forbidden;
      dispatch(
        devErrorAndLog(
          e.toString(),
          ERROR_TEXT_FETCH_PLANT_LIST,
          e,
          `region:${region} selectedProductLine: ${selectedProductLine}`,
          consoleOnly,
        ) as any,
      );
    }
  };
}

export function fetchPlantListAndMapFilters(
  region: string,
  selectedProductLine: ProductLine,
  userUrl?: string,
) {
  return async (dispatch: Dispatch, getState: any) => {
    try {
      if (!userUrl) {
        return;
      }

      // Check if user has TruckTrax Geo feature flag enabled
      const state = getState();
      const userPermission = state.userPermission || {};

      // If permissions haven't been loaded for this region yet, return early
      // This prevents incorrect default values when permissions load asynchronously
      if (!userPermission[region]) {
        return;
      }

      const isTruckTraxGeoEnabled = isFeatureFlagEnabled(
        userPermission,
        region,
        TRUCKTRAX_GEO_FLAG,
      );

      // these will be used if we can't find the user's preferences, e.g. on a user's first time using the app
      // showExtTrucks defaults to false for users without TruckTrax Geo, or true for users with TruckTrax Geo
      const defaultFilterValues = {
        showTrucks: true,
        showPlants: false,
        showPlantsGeofences: false,
        showJobsites: false,
        showJobsitesGeofences: false,
        showExtTrucks: isTruckTraxGeoEnabled,
      };

      // The preferences and the plants have no data dependency, so start both before
      // awaiting either. The plants rejection is caught here, at creation: the toggles come
      // from preferences alone, and a transient plants failure (getPlantList propagates
      // since DEV-4427) must not discard the user's saved filters back to
      // mapFilterInitialState. Catching here also keeps the rejection handled while the
      // preferences await runs.
      const plantsResult = getPlantList(region, selectedProductLine)
        .then(plants => ({ ok: true as const, plants }))
        .catch((e: any) => {
          if (e !== INVALID_STALE_TOKEN_LOGOUT) {
            const consoleOnly = e.response?.status === HTTP_CODES.forbidden;
            dispatch(
              devErrorAndLog(
                e.toString(),
                ERROR_TEXT_FETCH_PLANT_LIST,
                e,
                `region:${region} selectedProductLine: ${selectedProductLine}`,
                consoleOnly,
              ) as any,
            );
          }
          return { ok: false as const, plants: undefined };
        });

      const savedPreferences = await getUserPreferences(userUrl);
      const regionId = getIdFromUrl(region);
      const preferencesForRegionAndProductline =
        savedPreferences?.mapFilters?.[regionId]?.[selectedProductLine];

      const { showTrucks, showPlants, showPlantsGeofences, showJobsites, showJobsitesGeofences } =
        preferencesForRegionAndProductline || defaultFilterValues;

      // Only allow external trucks to be shown if the user has the TruckTrax Geo feature flag
      // Check if showExtTrucks exists in saved preferences - if it doesn't exist, use the default
      // If it exists (even if false), respect the saved value (as long as TruckTrax Geo is enabled)
      const savedShowExtTrucks = preferencesForRegionAndProductline?.showExtTrucks;
      let showExtTrucks: boolean;
      if (isTruckTraxGeoEnabled) {
        showExtTrucks =
          savedShowExtTrucks !== undefined ? savedShowExtTrucks : defaultFilterValues.showExtTrucks;
      } else {
        showExtTrucks = false;
      }

      const selectedPlantIds =
        preferencesForRegionAndProductline && preferencesForRegionAndProductline.selectedPlantIds;

      const plants = await plantsResult;
      const plantListForMapFilter = plants.ok
        ? (plants.plants ?? []).map((plant: any) => ({
            id: plant.id,
            name: plant.name,
            url: plant.url,
            // default to true if we weren't able to find selectedPlantIds
            isSelected: selectedPlantIds ? selectedPlantIds.includes(plant.id) : true,
          }))
        : undefined;

      if (plants.ok) {
        dispatch({
          type: SET_PLANT_LIST,
          payload: plants.plants,
        });
      }

      // One dispatch: the toggles always, the plant list only when the fetch succeeded, so
      // there is no intermediate render where the toggles are current but plantList still
      // holds the previous region's plants.
      dispatch({
        type: TOGGLE_MAP_FILTER,
        payload: {
          showTrucks,
          showPlants,
          showPlantsGeofences,
          showJobsites,
          showJobsitesGeofences,
          showExtTrucks, // Explicitly set to false when loading preferences to prevent unwanted saves
          ...(plantListForMapFilter ? { plantList: plantListForMapFilter } : {}),
        },
      });
    } catch (e: any) {
      if (e !== INVALID_STALE_TOKEN_LOGOUT) {
        const consoleOnly = e.response?.status === HTTP_CODES.forbidden;
        dispatch(
          devErrorAndLog(
            e.toString(),
            ERROR_TEXT_FETCH_PLANT_LIST,
            e,
            `region:${region} selectedProductLine: ${selectedProductLine}`,
            consoleOnly,
          ) as any,
        );
      }
    }
  };
}

export function getPlant(id: number, region?: string) {
  return async (dispatch: Dispatch) => {
    const url = `${getGeotraxBaseUrl() + PLANTS_PATH}/${id}`;
    const params: any = { isArchived: false };

    if (region) {
      params.regionUrl = region;
    }

    try {
      const response = await getRequest(url, params);
      return response.data;
    } catch (e: any) {
      if (e !== INVALID_STALE_TOKEN_LOGOUT) {
        const consoleOnly = e.response?.status === HTTP_CODES.forbidden;
        dispatch(
          devErrorAndLog(
            e.toString(),
            ERROR_TEXT_FETCH_PLANT,
            e,
            `url: ${url} params: ${JSON.stringify(params)}`,
            consoleOnly,
          ) as any,
        );
      }
      return undefined;
    }
  };
}

export const fetchPlant = async (id: number): Promise<PlantDto | undefined> => {
  const url = `${getGeotraxBaseUrl() + PLANTS_PATH}/${id}`;
  try {
    const response = await getRequest(url);
    return response ? response.data : undefined;
  } catch (e) {
    if (e !== INVALID_STALE_TOKEN_LOGOUT) {
      throw e;
    }
  }
  return undefined;
};

export async function fetchGeozoneList(
  region?: string,
  isDeleted?: boolean,
  isArchived?: boolean,
  inProductLines?: string,
) {
  const url = getGeotraxBaseUrl() + GEOZONES_PATH;
  const params: {
    region?: string;
    productLine?: string;
    isDeleted?: boolean;
    isArchived?: boolean;
    inProductLines?: string;
  } = {};

  if (region) {
    params.region = region;
  }
  if (inProductLines) {
    params.inProductLines = inProductLines;
  }

  params.isDeleted = !!isDeleted;
  params.isArchived = !!isArchived;
  let response;
  try {
    response = await getRequest(url, params);
  } catch (e: any) {
    recordLog(LogLevel.Error, `getGeozoneList Attempt: ${url}`, 'fail');
  }
  return response && response.data ? response.data.items : [];
}

export function setPlantList(plants: PlantDto[]) {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: SET_PLANT_LIST,
      payload: plants,
    });
  };
}
